/**
 * Turning a business item log's `data` blob into something a person can read.
 *
 * The API stores the log's metadata dictionary as JSON, and the interesting
 * part of it — the model the POS or the admin tools actually sent — is itself a
 * JSON *string* inside that dictionary. So the blob is double-encoded, its keys
 * are the C# property names, and every text value the POS sends is padded out
 * to its column width with spaces. Shown raw it is a wall of
 * `\"Aisle\":\"07C            \"` that nobody reads.
 *
 * This module unpacks it into labelled fields, and — because these logs are a
 * running record of one item — can say what changed between two of them, which
 * is the question anyone opening the drawer is actually asking.
 */

// ------------------------------------------------------------------- values

export type PayloadValue =
  | { kind: 'empty' }
  | { kind: 'bool'; value: boolean }
  | { kind: 'money'; value: number }
  | { kind: 'number'; value: number }
  | { kind: 'date'; value: string }
  | { kind: 'text'; value: string };

export interface PayloadField {
  key: string;
  label: string;
  value: PayloadValue;
}

export interface PayloadSection {
  /** The model the fields came from, or null for the log's own metadata. */
  name: string | null;
  label: string | null;
  fields: PayloadField[];
}

export interface LogPayload {
  sections: PayloadSection[];
  /** The blob re-serialised with indentation, for the raw view. */
  pretty: string;
}

export interface PayloadChange {
  key: string;
  label: string;
  before: PayloadValue;
  after: PayloadValue;
}

// ------------------------------------------------------------------- naming

/**
 * Keys the log row already shows above the payload, or that identify the
 * record rather than describe it. Repeating them inside the details is noise.
 */
const ENVELOPE_KEYS = new Set([
  'id',
  'createdat',
  'businessitemid',
  'itemid',
  'approverid',
  'description',
  'location',
  'isconnected',
  'businessid',
  'lastapplieditemupdates',
  'lastitemupdates',
]);

/** Words that should stay upper-case when a key is split for display. */
const ACRONYMS = new Set([
  'id',
  'upc',
  'gtin',
  'ebt',
  'pos',
  'sku',
  'gmo',
  'url',
  'api',
  'spa',
]);

/**
 * Plain-language names for the fields that come up most.
 *
 * Everything else falls back to splitting the C# name, which reads well enough
 * (`ItemNumber` → "Item number") but produces "Is visible" where "Visible in
 * store" is what the field actually means to the person reading it.
 */
const LABELS: Record<string, string> = {
  isvisible: 'Visible in store',
  isdeleted: 'Deleted',
  isebt: 'EBT eligible',
  idrequired: 'ID required',
  isalcoholicbeverage: 'Alcoholic beverage',
  isperishable: 'Perishable',
  ispackagingsensitive: 'Packaging sensitive',
  istemperaturesensitive: 'Temperature sensitive',
  hasrandomweight: 'Sold by weight',
  madelocally: 'Made locally',

  baseprice: 'Base price',
  basemultiple: 'Base multiple',
  basepricemethod: 'Base price method',
  promoprice: 'Promotional price',
  promomultiple: 'Promotional multiple',
  promopricemethod: 'Promotional price method',
  promostartdate: 'Promotion starts',
  promoenddate: 'Promotion ends',
  cost: 'Cost',
  pack: 'Pack size',
  unitpricefactor: 'Unit price factor',
  stepamount: 'Step amount',
  minquantity: 'Minimum quantity',
  maxquantity: 'Maximum quantity',
  statetaxpct: 'State tax %',
  muntaxpct: 'Municipal tax %',
  taxrule: 'Tax rule',

  editedat: 'Edited in POS',
  createdat: 'Created in POS',
  itemnumber: 'POS item number',
  internalname: 'Internal name',
  namespa: 'Name (Spanish)',
  descriptionspa: 'Description (Spanish)',
  searchtags: 'Search tags',
  salesrank: 'Sales rank',
  quantityonhand: 'Quantity on hand',
  weighttype: 'Weight type',
  force: 'Forced resend',
};

/** Model names, which arrive as the metadata key wrapping the payload. */
const SECTION_LABELS: Record<string, string> = {
  itempriceupdate: 'Item record sent by the POS',
  itempricebindingmodel: 'Price change submitted',
  itemdatabindingmodel: 'Item details submitted',
  businessitembindingmodel: 'Store item details submitted',
};

/** Numbers under these keys are money, not counts. */
const MONEY_KEYS = new Set([
  'baseprice',
  'promoprice',
  'cost',
  'price',
  'unitprice',
  'amount',
  'total',
  'subtotal',
  'posdiscountamount',
  'webstorediscountamount',
]);

/** `IsAlcoholicBeverage` → "Is alcoholic beverage". */
function humanize(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[\s_]+/)
    .filter(Boolean);

  if (words.length === 0) return key;

  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      return index === 0
        ? lower.charAt(0).toUpperCase() + lower.slice(1)
        : lower;
    })
    .join(' ');
}

export function fieldLabel(key: string): string {
  return LABELS[key.toLowerCase()] ?? humanize(key);
}

function sectionLabel(key: string): string {
  return SECTION_LABELS[key.toLowerCase()] ?? humanize(key);
}

// ------------------------------------------------------------------ parsing

/** Dates arrive as `2026-07-28T08:30:03`, with no zone marker. */
const ISO_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

function classify(key: string, value: unknown): PayloadValue {
  if (value === null || value === undefined) return { kind: 'empty' };

  if (typeof value === 'boolean') return { kind: 'bool', value };

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return { kind: 'empty' };
    return MONEY_KEYS.has(key.toLowerCase())
      ? { kind: 'money', value }
      : { kind: 'number', value };
  }

  if (typeof value === 'string') {
    // Every text value the POS sends is padded to its column width.
    const text = value.trim();
    if (text === '') return { kind: 'empty' };
    if (ISO_LOCAL.test(text)) return { kind: 'date', value: text };
    return { kind: 'text', value: text };
  }

  // Arrays and nested objects are rare here and have no better rendering.
  return { kind: 'text', value: JSON.stringify(value) };
}

/** A metadata value that is itself a serialised object. */
function asNestedObject(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text.startsWith('{')) return null;
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function toFields(source: Record<string, unknown>): PayloadField[] {
  return Object.entries(source)
    .filter(([key]) => !ENVELOPE_KEYS.has(key.toLowerCase()))
    .map(([key, value]) => ({
      key,
      label: fieldLabel(key),
      value: classify(key, value),
    }));
}

/**
 * Unpack a log's `data` blob. Returns null when there is nothing to show —
 * no data, or something that is not the JSON envelope we expect.
 */
export function parseLogPayload(raw: string | null | undefined): LogPayload | null {
  if (!raw || raw.trim() === '') return null;

  let envelope: unknown;
  try {
    envelope = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!envelope || typeof envelope !== 'object' || Array.isArray(envelope)) {
    return null;
  }

  const record = envelope as Record<string, unknown>;
  const loose: Record<string, unknown> = {};
  const sections: PayloadSection[] = [];

  for (const [key, value] of Object.entries(record)) {
    const nested = asNestedObject(value);
    if (nested) {
      sections.push({
        name: key,
        label: sectionLabel(key),
        fields: toFields(nested),
      });
    } else {
      loose[key] = value;
    }
  }

  const looseFields = toFields(loose);
  if (looseFields.length > 0) {
    sections.unshift({ name: null, label: null, fields: looseFields });
  }

  if (sections.length === 0) return null;

  // Re-serialise with the nested models expanded, so the raw view is readable
  // too rather than one line of escaped quotes.
  const expanded: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    expanded[key] = asNestedObject(value) ?? value;
  }

  return { sections, pretty: JSON.stringify(expanded, null, 2) };
}

// --------------------------------------------------------------------- diff

/** Same value, for the purpose of "did this update change anything". */
function sameValue(a: PayloadValue, b: PayloadValue): boolean {
  if (a.kind === 'empty' && b.kind === 'empty') return true;
  if (a.kind !== b.kind) return false;
  return 'value' in a && 'value' in b ? a.value === b.value : true;
}

const EMPTY: PayloadValue = { kind: 'empty' };

/**
 * What this log changed, compared with the one before it.
 *
 * Only sections present in both are compared: two logs can carry different
 * models (a price submission against a full POS record), and lining those up
 * field by field would report every difference between the two shapes as a
 * change, which is worse than saying nothing.
 */
export function diffPayloads(
  current: LogPayload,
  previous: LogPayload
): PayloadChange[] | null {
  const before = new Map(
    previous.sections.map((section) => [section.name, section])
  );

  const shared = current.sections.filter((section) => before.has(section.name));
  if (shared.length === 0) return null;

  const changes: PayloadChange[] = [];

  for (const section of shared) {
    const previousFields = new Map(
      before.get(section.name)!.fields.map((field) => [field.key, field.value])
    );

    for (const field of section.fields) {
      const was = previousFields.get(field.key) ?? EMPTY;
      if (!sameValue(was, field.value)) {
        changes.push({
          key: `${section.name ?? ''}.${field.key}`,
          label: field.label,
          before: was,
          after: field.value,
        });
      }
    }
  }

  return changes;
}

// ---------------------------------------------------------------- rendering

/**
 * Payload timestamps carry no zone marker and are written by the POS, so
 * unlike the log's own `createdAt` they are not known to be UTC. They are
 * rendered exactly as sent — shifting them into the reader's zone would move
 * the one number they are usually being read for.
 */
function formatPayloadDate(iso: string, locale: string): string {
  const parts = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!parts) return iso;

  const date = new Date(
    Number(parts[1]),
    Number(parts[2]) - 1,
    Number(parts[3]),
    Number(parts[4]),
    Number(parts[5])
  );
  if (Number.isNaN(date.getTime())) return iso;

  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function formatPayloadValue(
  value: PayloadValue,
  locale: string,
  words: { yes: string; no: string; empty: string }
): string {
  switch (value.kind) {
    case 'empty':
      return words.empty;
    case 'bool':
      return value.value ? words.yes : words.no;
    case 'money':
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
      }).format(value.value);
    case 'number':
      return new Intl.NumberFormat(locale).format(value.value);
    case 'date':
      return formatPayloadDate(value.value, locale);
    case 'text':
      return value.value;
  }
}
